export const INTERNAL_BROWSER_QUERY_KEYS = [
  "limit",
  "offset",
  "currentPage",
] as const;

const ISO_DATE_PARTS_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;
const JS_DATE_PARTS_REGEX =
  /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+)?(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(\d{4})(?:\s+.*)?$/i;
const MAX_FORTNIGHT_RANGE_DAYS = 31;

const MONTH_INDEX_BY_SHORT_NAME: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const SINGLE_VALUE_QUERY_KEYS = new Set<string>([
  ...INTERNAL_BROWSER_QUERY_KEYS,
  "sortBy",
  "orderBy",
  "search",
  "entity",
  "action",
]);

const BROWSER_PAIR_VALUE_QUERY_KEYS = new Set<string>([
  "dateRange",
  "fortnight",
]);

const getDaysInMonth = (year: number, monthIndex: number): number => {
  return new Date(year, monthIndex + 1, 0).getDate();
};

const isValidCalendarDateParts = (
  year: number,
  monthIndex: number,
  day: number,
): boolean => {
  return (
    Number.isInteger(year) &&
    Number.isInteger(monthIndex) &&
    Number.isInteger(day) &&
    monthIndex >= 0 &&
    monthIndex <= 11 &&
    day >= 1 &&
    day <= getDaysInMonth(year, monthIndex)
  );
};

const parseIsoDateQueryValue = (dateValue: string): Date | null => {
  const match = dateValue.match(ISO_DATE_PARTS_REGEX);

  if (!match) {
    return null;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const monthIndex = Number(monthValue) - 1;
  const day = Number(dayValue);

  if (!isValidCalendarDateParts(year, monthIndex, day)) {
    return null;
  }

  const parsedDate = new Date(dateValue);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const parseJsDateQueryValue = (dateValue: string): Date | null => {
  const match = dateValue.match(JS_DATE_PARTS_REGEX);

  if (!match) {
    return null;
  }

  const [, monthValue, dayValue, yearValue] = match;
  const monthIndex = MONTH_INDEX_BY_SHORT_NAME[monthValue.toLowerCase()];
  const year = Number(yearValue);
  const day = Number(dayValue);

  if (!isValidCalendarDateParts(year, monthIndex, day)) {
    return null;
  }

  const parsedDate = new Date(dateValue);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Parses date strings accepted from browser-visible query params without
 * allowing JavaScript Date normalization such as "May 100".
 *
 * @param {string | null | undefined} dateValue - Raw decoded query value.
 * @returns {Date | null} Parsed date when the raw value is a real calendar date.
 */
export const parseBrowserDateQueryValue = (
  dateValue: string | null | undefined,
): Date | null => {
  const normalizedDateValue = dateValue?.trim();

  if (!normalizedDateValue) {
    return null;
  }

  return (
    parseIsoDateQueryValue(normalizedDateValue) ??
    parseJsDateQueryValue(normalizedDateValue)
  );
};

const isValidFortnightRange = (startDate: Date, endDate: Date): boolean => {
  const rangeInDays = Math.round(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  return rangeInDays >= 0 && rangeInDays <= MAX_FORTNIGHT_RANGE_DAYS;
};

/**
 * Checks whether range-style browser query values are parseable calendar dates.
 *
 * @param {string} key - Browser query key.
 * @param {string[]} dateValues - Decoded query values for the key.
 * @returns {boolean} True when present values are safe date values.
 */
export const areValidBrowserDateRangeValues = (
  key: string,
  dateValues: string[],
): boolean => {
  const rangeValues = dateValues.slice(0, 2);

  if (!BROWSER_PAIR_VALUE_QUERY_KEYS.has(key) || rangeValues.length === 0) {
    return true;
  }

  const parsedDates = rangeValues.map(parseBrowserDateQueryValue);

  if (parsedDates.some((parsedDate) => !parsedDate)) {
    return false;
  }

  const [startDate, endDate] = parsedDates;

  if (key !== "fortnight" || !startDate || !endDate) {
    return true;
  }

  return isValidFortnightRange(startDate, endDate);
};

const normalizeQueryString = (filter: string | undefined): string => {
  return filter?.replace(/^[?&]+/, "") ?? "";
};

/**
 * Checks whether a query key is internal browser-only state.
 *
 * @param {string} key - Query param key to check.
 * @returns {boolean} True when the key should not be visible in browser URLs.
 */
export const isInternalBrowserQueryKey = (key: string): boolean => {
  return INTERNAL_BROWSER_QUERY_KEYS.includes(
    key as (typeof INTERNAL_BROWSER_QUERY_KEYS)[number],
  );
};

/**
 * Appends a query string into a target params object without duplicating
 * singleton keys such as pagination and sort values.
 *
 * @param {URLSearchParams} targetParams - Params object to mutate.
 * @param {string | undefined} filter - Query string fragment to merge.
 * @returns {void}
 */
export const appendSanitizedQueryString = (
  targetParams: URLSearchParams,
  filter: string | undefined,
): void => {
  const filterParams = new URLSearchParams(normalizeQueryString(filter));

  filterParams.forEach((queryValue, key) => {
    if (!key || queryValue === "") {
      return;
    }

    if (SINGLE_VALUE_QUERY_KEYS.has(key)) {
      targetParams.set(key, queryValue);

      return;
    }

    targetParams.append(key, queryValue);
  });
};

/**
 * Removes internal app state from browser-visible query params.
 *
 * @param {URLSearchParams} searchParams - Browser search params.
 * @returns {URLSearchParams} Query params with internal keys removed.
 */
export const sanitizeBrowserQueryParams = (
  searchParams: URLSearchParams,
): URLSearchParams => {
  const sanitizedParams = new URLSearchParams();
  const browserPairCounts = new Map<string, number>();

  searchParams.forEach((queryValue, key) => {
    if (!key || queryValue === "" || isInternalBrowserQueryKey(key)) {
      return;
    }

    if (SINGLE_VALUE_QUERY_KEYS.has(key)) {
      sanitizedParams.set(key, queryValue);

      return;
    }

    if (BROWSER_PAIR_VALUE_QUERY_KEYS.has(key)) {
      const currentCount = browserPairCounts.get(key) ?? 0;

      if (currentCount >= 2) {
        return;
      }

      browserPairCounts.set(key, currentCount + 1);
    }

    sanitizedParams.append(key, queryValue);
  });

  return sanitizedParams;
};

/**
 * Detects invalid browser-visible query params before feature code reads them.
 *
 * @param {URLSearchParams} searchParams - Browser search params.
 * @returns {boolean} True when any range param contains an invalid date value.
 */
export const hasInvalidBrowserQueryParams = (
  searchParams: URLSearchParams,
): boolean => {
  return Array.from(BROWSER_PAIR_VALUE_QUERY_KEYS).some((key) => {
    return !areValidBrowserDateRangeValues(key, searchParams.getAll(key));
  });
};

/**
 * Builds URLSearchParams by merging a legacy filter string and explicit params.
 *
 * @param {string | undefined} filter - Existing query filter string.
 * @param {Record<string, QueryParamValue>} params - Params to merge.
 * @returns {URLSearchParams} Merged query params.
 */
export const buildQueryParams = (
  filter: string | undefined,
  params: Record<string, QueryParamValue> = {},
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  appendSanitizedQueryString(searchParams, filter);

  Object.entries(params).forEach(([key, queryParamValue]) => {
    if (
      queryParamValue === undefined ||
      queryParamValue === null ||
      queryParamValue === ""
    ) {
      return;
    }

    if (Array.isArray(queryParamValue)) {
      searchParams.delete(key);
      queryParamValue.forEach((queryParamItem) => {
        if (
          queryParamItem === undefined ||
          queryParamItem === null ||
          queryParamItem === ""
        ) {
          return;
        }

        searchParams.append(key, String(queryParamItem));
      });

      return;
    }

    searchParams.set(key, String(queryParamValue));
  });

  return searchParams;
};
